import { isDemoMode } from "@/lib/demo/demoMode";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FileText, Link2, MessageSquareText, StickyNote, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fieldsAsMarkdown, knowledgeItemAsMarkdown, markdownToFields, type KnowledgeItem, typeLabels, validateKnowledgeItem } from "./model";
import ContactAssociation from "./ContactAssociation";

interface Props { item: KnowledgeItem; onSave: (item: KnowledgeItem) => void; onOpenFile: (item: KnowledgeItem) => void; onCancel: () => void; saving?: boolean }

const editorTypeMeta: Record<KnowledgeItem["type"], { Icon: LucideIcon; label: string; color: string }> = {
  file: { Icon: FileText, label: "File", color: "text-muted-foreground" },
  note: { Icon: StickyNote, label: "Note", color: "text-yellow-500 dark:text-yellow-400" },
  website: { Icon: Link2, label: "URL", color: "text-violet-600 dark:text-violet-400" },
  faq: { Icon: MessageSquareText, label: "Instruction", color: "text-primary" },
  instructions: { Icon: MessageSquareText, label: "Instruction", color: "text-primary" },
  offer: { Icon: MessageSquareText, label: "Instruction", color: "text-primary" },
};
const markdownDocumentTypes = new Set<KnowledgeItem["type"]>(["faq", "instructions", "offer"]);

export default function KnowledgeItemEditor({ item, onSave, onOpenFile, onCancel, saving = false }: Props) {
  const [searchParams] = useSearchParams();
  const contactProfileParams = new URLSearchParams(searchParams);
  contactProfileParams.set("tab", "customer-details");
  contactProfileParams.delete("contact");
  contactProfileParams.delete("id");
  contactProfileParams.delete("graph");
  if (item.associatedContact) {
    contactProfileParams.set("id", item.associatedContact.customerId);
    if (item.associatedContact.contactId) contactProfileParams.set("graph", item.associatedContact.contactId);
  }
  const [draft, setDraft] = useState<KnowledgeItem>(() => markdownDocumentTypes.has(item.type) ? {
    ...item,
    content: knowledgeItemAsMarkdown(item),
    tone: undefined,
    responseLength: undefined,
    language: undefined,
    avoid: undefined,
    handover: undefined,
    questions: undefined,
    validFrom: undefined,
    validUntil: undefined,
  } : item);
  const [error, setError] = useState<string | null>(null);
  const { Icon: TypeIcon, label: typeLabel, color: typeColor } = editorTypeMeta[draft.type];
  const markdownFields = markdownDocumentTypes.has(draft.type) ? markdownToFields(draft.content) : [];
  const markdownPlaceholder = draft.type === "faq"
    ? "## What do customers ask?\n\nWrite the answer…"
    : draft.type === "offer"
    ? "## Offer details\n\nDescribe the offer…"
    : "## Tone of voice\n\nWrite your instructions…";
  const set = (patch: Partial<KnowledgeItem>) => setDraft(value => ({ ...value, ...patch }));
  const textField = (key: "content" | "avoid" | "handover", label: string, placeholder: string, rows = 4) => (
    <div className="space-y-2">
      <Label htmlFor={`knowledge-${key}`}>{label}</Label>
      <Textarea className="scrollbar-hide" id={`knowledge-${key}`} rows={rows} readOnly={!!item.associatedContact} value={draft[key] ?? ""} placeholder={placeholder} onChange={e => set({ [key]: e.target.value })} />
    </div>
  );

  return <form className="flex h-full min-h-0 flex-col" onSubmit={e => {
    e.preventDefault();
    if (item.associatedContact) return;
    const message = validateKnowledgeItem(draft);
    setError(message);
    if (!message) onSave(draft);
  }}>
    <SheetHeader className="border-b pb-5 pr-6 text-left">
      <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><TypeIcon className={`h-3.5 w-3.5 ${typeColor}`} aria-hidden="true" />{typeLabel}</p>
      <SheetTitle className="font-heading">{item.name || `New ${typeLabels[draft.type].toLowerCase()}`}</SheetTitle>
      <SheetDescription className={item.associatedContact ? "sr-only" : undefined}>{item.associatedContact ? `${typeLabel} details` : draft.type === "instructions" ? "Choose how your AI speaks and when it asks for help." : "Review and organise the information your AI can reference."}</SheetDescription>
      <ContactAssociation item={item} />
    </SheetHeader>
    <fieldset disabled={saving || isDemoMode()} className="scrollbar-hide -mx-1.5 min-h-0 flex-1 space-y-6 overflow-y-auto px-1.5 py-6">
      {draft.type === "file" && item.hasOriginalFile !== false && !item.isMarkdownFile && <button type="button" onClick={() => onOpenFile(item)} aria-label={`Open ${item.name}`} className="flex w-full items-center gap-3 rounded-lg border p-4 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <FileText className="h-6 w-6 shrink-0 text-muted-foreground" />
        <div className="min-w-0"><p className="truncate text-sm font-medium">{item.name}</p><p className="text-xs text-muted-foreground">Open original file</p></div>
      </button>}
      <div className="space-y-2">
        <Label htmlFor="knowledge-name">File name</Label>
        <Input id="knowledge-name" value={draft.name} readOnly={!!item.associatedContact} onChange={e => set({ name: e.target.value })} placeholder={draft.type === "faq" ? "e.g. Admissions FAQs" : "Give this file a name"} />
      </div>
      {draft.type === "file" && item.isMarkdownFile && textField("content", "Markdown", "Write the reply scripts in Markdown…", 18)}
      {draft.type === "file" && !item.isMarkdownFile && <>
        <div className="space-y-2"><Label>Extracted text</Label>
          <div className="min-h-48 whitespace-pre-wrap break-words rounded-lg border bg-muted/20 p-4 text-sm leading-6">
            {item.content || (item.associatedContact ? item.status === "Processing" ? "This file is being processed. Its extracted text will appear when ready." : "No extracted text is available. Open the contact profile to review this file." : "No extracted text is available. Retry processing from the item’s menu.")}
          </div>
        </div>
      </>}
      {draft.type === "note" && textField("content", "Content", "Write about your business, services, policies, or anything your AI should know…", 16)}
      {draft.type === "website" && <>
        <div className="space-y-2"><Label htmlFor="knowledge-url">Website URL</Label><Input id="knowledge-url" type="url" value={draft.url ?? ""} onChange={e => set({ url: e.target.value })} placeholder="https://yourbusiness.com" /></div>
        <div className="rounded-lg border border-dashed p-5 text-sm text-muted-foreground">Dibs imports the readable text from this public page. Use Refresh content from the item’s menu when the page changes. Links to other pages are not imported automatically.</div>
      </>}
      {markdownDocumentTypes.has(draft.type) && <Tabs defaultValue="fields" className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <Label>Content</Label>
          <TabsList className="h-8">
            <TabsTrigger value="fields" className="h-6 px-2.5 text-xs">Fields</TabsTrigger>
            <TabsTrigger value="markdown" className="h-6 px-2.5 text-xs">Markdown</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="fields" className="mt-0 space-y-4">
          {markdownFields.map((field, index) => <div key={`${index}-${field.title}`} className="space-y-2">
            <Label htmlFor={`knowledge-field-content-${index}`}>{field.title}</Label>
            <Textarea
              id={`knowledge-field-content-${index}`}
              className="min-h-20 text-sm leading-6"
              rows={Math.max(3, Math.min(12, field.content.split("\n").length + 1))}
              value={field.content}
              placeholder="Field content"
              onChange={e => set({ content: fieldsAsMarkdown(markdownFields.map((entry, fieldIndex) => fieldIndex === index ? { ...entry, content: e.target.value } : entry)) })}
            />
          </div>)}
        </TabsContent>
        <TabsContent value="markdown" className="mt-0 space-y-2">
          <Textarea id="knowledge-instructions-content" aria-label="Content in Markdown" className="min-h-[420px] font-mono text-sm leading-6" value={draft.content} placeholder={markdownPlaceholder} onChange={e => set({ content: e.target.value })} />
          <p className="text-xs text-muted-foreground">Use level-two headings (<code>##</code>) to create fields.</p>
        </TabsContent>
      </Tabs>}
    </fieldset>
    <div className="space-y-3 border-t pt-4">
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <div className="flex justify-end gap-2"><Button type="button" variant="outline" disabled={saving} onClick={onCancel}>{item.associatedContact ? "Close" : "Cancel"}</Button>{item.associatedContact ? <Button asChild><Link to={`?${contactProfileParams.toString()}`}>Manage in contact profile</Link></Button> : <Button type="submit" disabled={saving || isDemoMode()}>{saving ? "Saving…" : "Save"}</Button>}</div>
    </div>
  </form>;
}
